import React from "react";
import { Helmet } from "react-helmet";

const CoCurricularPage = ({ title, description, content, contentSecondary, images, pdf }) => {
  const [selectedImage, setSelectedImage] = React.useState(null);

  return (
    <>
      <Helmet>
        <title>{title} - VBU MCA</title>
      </Helmet>
      <div className="PageContainer">
        <div id="Heading">
          <h1>{title}</h1>
        </div>
        <div className="CoCurricularContent" data-aos="fade-up">
          <p className="CoCurricularDesc" style={{ fontSize: window.innerWidth < 769 ? "1.1rem" : "1.2rem", color: "#555", marginBottom: "20px" }}>{description}</p>
          <div className="MainContent">
            <p style={{ marginBottom: "15px", lineHeight: "1.6" }}>{content}</p>
            {contentSecondary && <p style={{ lineHeight: "1.6" }}>{contentSecondary}</p>}
          </div>

          {pdf && (
            <div className="PdfLink" style={{ marginTop: "20px" }}>
              <a 
                href={pdf} 
                target="_blank" 
                rel="noreferrer" 
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  gap: "10px", 
                  background: "#d32f2f", 
                  color: "white", 
                  padding: "10px 20px", 
                  borderRadius: "5px", 
                  fontWeight: "600",
                  textDecoration: "none"
                }}
              >
                View Official Document (PDF)
              </a>
            </div>
          )}

          {images && images.length > 0 && (
            <div className="ImageGallery" style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", 
              gap: window.innerWidth < 769 ? "10px" : "20px", 
              marginTop: "40px" 
            }}>
              {images.map((img, index) => (
                <div 
                  key={index} 
                  className="GalleryItem" 
                  onClick={() => setSelectedImage(img)}
                  style={{ 
                    borderRadius: "12px", 
                    overflow: "hidden", 
                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                    transition: "transform 0.3s ease",
                    cursor: "zoom-in"
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.02)"}
                  onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
                >
                  <img 
                    src={img} 
                    alt={`${title} Gallery ${index + 1}`} 
                    style={{ width: "100%", height: window.innerWidth < 769 ? "140px" : "250px", objectFit: "cover", display: "block" }} 
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal Overlay */}
      {selectedImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.95)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            cursor: 'zoom-out',
            padding: '20px'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <span 
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              color: 'white',
              fontSize: '40px',
              fontWeight: 'bold',
              cursor: 'pointer',
              zIndex: 10
            }}
          >
            &times;
          </span>
          <img 
            src={selectedImage} 
            alt="Full view" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain',
              borderRadius: '4px',
              boxShadow: '0 0 30px rgba(255,255,255,0.1)',
              animation: 'fadeIn 0.3s ease'
            }} 
          />
        </div>
      )}
    </>
  );
};

export const Sports = () => (
  <CoCurricularPage 
    title="Sports" 
    description="Physical fitness and sportsmanship are integral parts of our student's holistic development. Our department encourages active participation in various indoor and outdoor sports activities alongside standard academics."
    content="We organize highly anticipated annual sports meets, encouraging physical well-being and competitive spirit among the MCA students. Students regularly participate in university-level tournaments for Cricket, Football, Volleyball, Badminton, and Athletics. The department actively utilizes the expansive Campus Ground for outdoor events."
    contentSecondary="In addition to field sports, we promote indoor games like Table Tennis, Carrom, and Chess to foster strategic thinking and mental agility. Regular sporting events not only help in team-building among peers but also ensure a healthy balance between rigorous computer applications coursework and physical fitness."
  />
);

export const NCC = () => (
  <CoCurricularPage 
    title="NCC (National Cadet Corps)" 
    description="The department provides exciting opportunities for students to join the National Cadet Corps (NCC), rigorously fostering character, comradeship, discipline, and the spirit of selfless service to the nation."
    content="Our cadets actively participate in national-level camps (such as CATC and RDC), military training protocols, and rigorous physical exercises. The curriculum covers a widespread range of subjects, including drill, weapon training, map reading, and civil defense, thereby inherently developing distinguished leadership qualities and a deep sense of national pride among tech students."
    contentSecondary="Many of our cadets proudly pass the prestigious 'B' and 'C' Certificate examinations, opening doors to highly reputed career opportunities in the Armed Forces and paramilitary organizations. The core objective is to create organized, trained, and motivated youth to provide leadership in all walks of life."
  />
);

export const NSS = () => (
  <CoCurricularPage 
    title="NSS (National Service Scheme)" 
    description="The NSS unit in our department focuses enthusiastically on community service and social responsibility, encouraging MCA students to contribute meaningfully to societal development under the primary motto: 'Not Me But You'."
    content="Our student volunteers organize and participate in a wide variety of impactful activities. Regular events include voluntary blood donation camps on the university campus, literacy drives for underprivileged children in adjoining localities, environmental awareness campaigns, Swachh Bharat Abhiyan clean-up drives, and massive tree plantation initiatives during the monsoon."
    contentSecondary="Furthermore, the NSS wing frequently conducts 7-day Special Camps in officially adopted villages around the Hazaribagh district. During these camps, students conduct surveys, resolve fundamental digital issues, raise awareness against social evils, and actively contribute to the sustainable development of the rural populace."
  />
);

export const NCDRC = () => (
  <CoCurricularPage 
    title="NCDRC (National Cyber Defense Resource Center)" 
    description="The National Cyber Defense Resource Center (NCDRC) initiative at VBU focuses specifically on cutting-edge cyber security awareness and advanced technical training for future IT professionals."
    content="In an era where data breaches and hacking are prevalent, we conduct intensive hands-on workshops and elite certification programs in collaboration with renowned cyber security experts from across the nation. The key focus areas include Ethical Hacking, Network Security, Cryptography, and Digital Forensics."
    contentSecondary="The NCDRC acts as a pivotal bridge between academic learning and real-world digital security requirements. Regular seminars on Cyber Laws, online financial frauds, and data privacy actively prepare our MCA students to face the evolving global challenges in digital safety, rendering them exceptionally employable in the information security sector."
  />
);

export const UBA = () => (
  <CoCurricularPage 
    title="Unnat Bharat Abhiyan (UBA)" 
    description="Unnat Bharat Abhiyan (UBA) is a flagship program of the Ministry of Education, Government of India, with the fundamental objective of securely connecting higher education institutions with rural communities."
    content="Our department actively participates in identifying pressing developmental challenges in adopted rural areas within the Hazaribagh periphery and providing appropriate technological solutions for sustainable development. Our students undertake crucial extensive fieldwork to seamlessly integrate their classroom knowledge into practically solving village-level problems."
    contentSecondary="Under the UBA banner, the MCA students spearhead digital literacy campaigns to educate villagers on seamlessly utilizing e-governance portals, mobile banking applications, and secure internet practices. This directly aligns with the vision of transformational change in rural development processes by leveraging the robust knowledge base of academic institutions."
  />
);

export const WomenEmpowerment = () => (
  <CoCurricularPage 
    title="Women Empowerment Cell" 
    description="The strategically established Women Empowerment Cell intrinsically aims to create a highly gender-sensitive environment and empower female students and staff through various sensitization and skill-building programs."
    content="We are deeply committed to maintaining a safe, supportive, and completely unbiased educational environment for women in technology. We consistently organize major seminars on legal rights, vital self-defense workshops, health and hygiene awareness camps, and distinguished guest lectures by inspiring female technocrats."
    contentSecondary="A dedicated grievance redressal mechanism ensures that any issues are handled with strict confidentiality and priority. The cell also actively leads the grand celebrations of International Women's Day on campus, honoring the extraordinary achievements of women and fundamentally challenging stereotypical gender norms within the tech industry."
  />
);

export const InductionProgram = () => (
  <CoCurricularPage 
    title="Induction Program" 
    description="The Induction Program is a crucial formal event designed to welcome new students into the University Department of Computer Applications, helping them transition seamlessly into their academic journey."
    content="During the Induction Program, students are introduced to the department's culture, academic expectations, and the wide array of resources available to them. It is an opportunity for freshers to interact with faculty members, senior students, and distinguished alumni, fostering a sense of community from day one."
    contentSecondary="The program includes orientation sessions on the MCA curriculum, introduction to various technical and co-curricular clubs, and inspirational talks by academic leaders. This event sets the foundation for a productive and engaging academic experience, ensuring that every student feels valued and motivated to achieve their best."
    images={[
      "/Images/inductionmeet/WhatsApp Image 2026-04-16 at 12.05.57.jpeg",
      "/Images/inductionmeet/WhatsApp Image 2026-04-16 at 12.05.58.jpeg",
      "/Images/inductionmeet/WhatsApp Image 2026-04-16 at 12.05.58 (1).jpeg"
    ]}
    pdf="/Images/inductionmeet/induction.pdf"
  />
);
